import React, { Component } from 'react';
import ReactPaginate from 'react-paginate';
import '../Pagination.css';
import '../common/MdPage.css';
import * as constantClass from '../fileWithConstants';
import { createPagedGridController } from '../../hooks/pagedGridClassAdapter';
import { displayDate } from '../../services/dates/dateService';
import * as actasService from '../../services/actas/actasService';
import {
  classifyActasError,
  downloadPdfFromBase64,
  formatActaExamenEstado,
  formatActaExamenPeriodoGrid,
  formatActaExamenTipo,
} from '../../services/actas/actasDto';
import { ConfirmDialog } from '../common/ConfirmDialog';
import {
  MdRowActions,
  MdTableActionsCell,
  MdTableCell,
} from '../common/MdRowActions';
import { SearchIcon } from '../common/mdPageIcons';

export class MDActaExamen extends Component {
  static displayName = MDActaExamen.name;

  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      searchInput: '',
      perPage: 10,
      paginationData: [],
      pageCount: 0,
      forcePage: 0,
      showErrorMessage: false,
      errorMessage: '',
      successMessage: '',
      closingId: null,
      pendingClose: null,
    };
    this.grid = createPagedGridController({
      fetchPage: (query) => actasService.listActasExamen(query),
      defaultPageSize: 10,
      onChange: (gridState) => this.applyGridState(gridState),
    });
    this.searchInputRef = React.createRef();
  }

  applyGridState(gridState) {
    this.setState({
      paginationData: gridState.items,
      pageCount: gridState.pageCount,
      searchInput: gridState.search,
      perPage: gridState.pageSize,
      loading: gridState.loading,
      forcePage: gridState.forcePage,
    });
  }

  async componentDidMount() {
    const canAccess = await constantClass.canAccess('WWActasExamen');
    if (canAccess) {
      this.populateData();
    }
  }

  handlePageClick = (e) => {
    this.grid.handlePageClick(e).catch((error) => this.handleActasError(error));
  };

  handleSearchInputChange = (e) => {
    const searchInput = e.target.value;
    const cleared = searchInput.trim() === '';
    this.setState({ searchInput, successMessage: '', showErrorMessage: false, errorMessage: '' });
    if (cleared && this.grid.getState().search.trim() !== '') {
      this.grid.clearSearchIfNeeded(this.grid.getState().search).catch((error) => this.handleActasError(error));
    }
  };

  handleSearchSubmit = (e) => {
    e.preventDefault();
    const search = (this.searchInputRef.current
      ? this.searchInputRef.current.value
      : this.state.searchInput).trim();
    this.setState({ searchInput: search });
    this.grid.submitSearch(search).catch((error) => this.handleActasError(error));
  };

  handleCloseRequest = (acta) => {
    if (this.state.closingId) {
      return;
    }
    this.setState({
      pendingClose: {
        id: acta.actaExamenNacta,
        nombre: (acta.materiaCarreraNombre || '').trim(),
      },
    });
  };

  handleCancelClose = () => {
    if (this.state.closingId) {
      return;
    }
    this.setState({ pendingClose: null });
  };

  handleConfirmClose = () => {
    const { pendingClose } = this.state;
    if (!pendingClose || this.state.closingId) {
      return;
    }
    this.cerrarActa(pendingClose.id);
  };

  buildRowActions(acta) {
    const id = acta.actaExamenNacta;
    const estado = String(acta.actaExamenEstado || '').trim();
    const actions = [];

    actions.push({
      key: 'watch',
      label: 'Ver Acta',
      variant: 'outline',
      icon: 'eye',
      to: '/aex/watch/' + id,
    });

    if (estado === 'ISN') {
      actions.push({
        key: 'load',
        label: 'Cargar Resultados',
        variant: 'primary',
        icon: 'pencil',
        to: '/aex/load/' + id,
      });
    }

    if (estado === 'Cargada') {
      actions.push({
        key: 'close',
        label: 'Cerrar',
        variant: 'primary',
        icon: 'lock',
        onClick: () => this.handleCloseRequest(acta),
        disabled: Boolean(this.state.closingId),
      });
    }

    if (estado === 'Pendiente') {
      actions.push({
        key: 'print',
        label: 'Imprimir Acta',
        variant: 'outline',
        icon: 'printer',
        onClick: () => this.imprimirActa(id),
        disabled: this.state.loading,
      });
    }

    actions.push({
      key: 'header',
      label: 'Modificar Encabezado',
      variant: 'outline',
      icon: 'pencil',
      to: '/update-eae/' + id,
    });

    return actions;
  }

  renderTable(actas) {
    return (
      <div className="md-page__table-card md-aex__table-card">
        <div className="md-aex__table-scroll">
          <table className="md-page__table md-page__table--actions-fixed md-page__table--aex-grid" aria-labelledby="md-aex-title">
            <thead>
              <tr>
                <th scope="col" className="md-page__table__col-materia">Materia</th>
                <th scope="col" className="md-page__table__col-carrera">Carrera</th>
                <th scope="col" className="md-page__table__col-periodo-examen">Período de examen</th>
                <th scope="col" className="md-page__table__col-fecha-examen">Fecha de examen</th>
                <th scope="col" className="md-page__table__col-estado">Estado acta</th>
                <th scope="col" className="md-page__table__col-condicion">Tipo acta</th>
                <th scope="col" className="md-page__table__actions-header" aria-label="Acciones" />
              </tr>
            </thead>
            <tbody>
              {actas.map((acta) => (
                <tr key={acta.actaExamenNacta}>
                  <MdTableCell className="md-page__table__col-materia">{acta.materiaCarreraNombre}</MdTableCell>
                  <MdTableCell className="md-page__table__col-carrera">{acta.carreraNombre}</MdTableCell>
                  <MdTableCell className="md-page__table__col-periodo-examen">
                    {formatActaExamenPeriodoGrid(acta)}
                  </MdTableCell>
                  <MdTableCell className="md-page__table__col-fecha-examen">
                    {displayDate(acta.mesaExamenFechaExamen)}
                  </MdTableCell>
                  <MdTableCell className="md-page__table__col-estado">
                    {formatActaExamenEstado(acta.actaExamenEstado)}
                  </MdTableCell>
                  <MdTableCell className="md-page__table__col-condicion">
                    {formatActaExamenTipo(acta.actaExamenTipo)}
                  </MdTableCell>
                  <MdTableActionsCell>
                    <MdRowActions items={this.buildRowActions(acta)} />
                  </MdTableActionsCell>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  render() {
    const isBusy = this.state.loading;
    const listLoading = this.state.loading && !this.state.closingId;
    const { pendingClose } = this.state;

    const contents = listLoading
      ? <p className="md-page__loading"><em>Cargando datos...</em></p>
      : this.renderTable(this.state.paginationData);

    return (
      <div className="md-page md-aex">
        <div className="md-page__header">
          <h1 id="md-aex-title" className="md-page__title">Manejar Datos de Actas de Examen</h1>
        </div>

        <div className="md-page__feedback" aria-live="polite" role="status">
          {this.state.showErrorMessage ? (
            <div className="md-page__error" role="alert">{this.state.errorMessage}</div>
          ) : null}
          {this.state.successMessage ? (
            <div className="md-page__success">{this.state.successMessage}</div>
          ) : null}
        </div>

        {!this.state.showErrorMessage ? (
          <>
            <form className="md-page__search-row" onSubmit={this.handleSearchSubmit}>
              <div className="md-page__search-wrap">
                <span className="md-page__search-icon">
                  <SearchIcon />
                </span>
                <input
                  ref={this.searchInputRef}
                  className="md-page__search-input"
                  type="text"
                  name="search"
                  autoComplete="off"
                  disabled={isBusy}
                  placeholder="Ingrese materia, carrera o estado del acta"
                  value={this.state.searchInput}
                  onChange={this.handleSearchInputChange}
                />
              </div>
              <button type="submit" className="md-page__search-btn" disabled={isBusy}>
                Buscar
              </button>
            </form>

            {contents}

            {!listLoading && this.state.pageCount > 1 ? (
              <div className="md-page__pagination">
                <ReactPaginate
                  previousLabel="Anterior"
                  nextLabel="Siguiente"
                  breakLabel="..."
                  breakClassName="break-me"
                  pageCount={this.state.pageCount}
                  marginPagesDisplayed={2}
                  pageRangeDisplayed={5}
                  onPageChange={this.handlePageClick}
                  forcePage={this.state.forcePage}
                  containerClassName="pagination"
                  subContainerClassName="pages pagination"
                  activeClassName="active"
                />
              </div>
            ) : null}
          </>
        ) : null}

        <ConfirmDialog
          open={Boolean(pendingClose)}
          title="Cerrar acta"
          message="Va a cerrar el acta, ¿está seguro?"
          detail={pendingClose ? pendingClose.nombre : ''}
          confirmLabel="Cerrar"
          cancelLabel="Cancelar"
          variant="primary"
          loading={Boolean(this.state.closingId)}
          onConfirm={this.handleConfirmClose}
          onCancel={this.handleCancelClose}
        />
      </div>
    );
  }

  handleActasError(error) {
    const outcome = classifyActasError(error);
    if (outcome.type === 'timeout') {
      constantClass.RedirectToLogin();
      return;
    }
    this.setState({
      showErrorMessage: true,
      errorMessage: outcome.message,
      loading: false,
    });
  }

  async populateData() {
    try {
      await this.grid.load();
    } catch (error) {
      this.handleActasError(error);
    }
  }

  async imprimirActa(id) {
    this.setState({ loading: true, showErrorMessage: false, errorMessage: '' });
    try {
      const pdfBase64 = await actasService.imprimirActaExamen(id);
      downloadPdfFromBase64(pdfBase64, 'actaExamen.pdf');
      this.setState({ successMessage: 'El acta se imprimió correctamente.' });
      await this.grid.reloadAfterDelete();
    } catch (error) {
      this.handleActasError(error);
    }
  }

  async cerrarActa(id) {
    this.setState({ closingId: id, showErrorMessage: false, errorMessage: '' });
    try {
      await actasService.cerrarActaExamen(id);
      this.setState({ successMessage: 'El acta se cerró correctamente.', pendingClose: null });
      await this.grid.reloadAfterDelete(this.state.paginationData.length);
    } catch (error) {
      this.handleActasError(error);
    } finally {
      this.setState({ closingId: null });
    }
  }
}
